import { Link, Outlet, useLocation } from "react-router-dom";
import React, { memo, useState, useEffect } from "react";
import PopupHeader from "../components/PopupHeader";
import PillButton from "../components/button/Pills";
import { adminSidebarItems } from "../config/sideBarItems";
import getDeviceId from "../utility/getDeviceId";
import authManager from "../services/auth/AuthManager";
import { resolveAutoBrand } from "../components/BrandLogo";

const SuperAdminLayouts = memo(() => {
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  const user = 'Super Admin';

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDark]);

  const handleLogout = () => {
    const deviceId = getDeviceId();
    authManager.logout(deviceId);
  };
  return (
    <>
      {/* Header */}
      <div className="fixed w-full z-50 flex bg-ez-header border-b border-ez-border text-ez-text-primary items-center h-16 px-6">
        <div className="ml-20 flex-none font-bold flex items-center h-full">
          <span className="text-xl font-bold text-ez-text-primary">
            Direct-to-Customer Messaging Admin
          </span>
        </div>
        <div className="grow" />
        <div className="flex-none flex items-center gap-4 h-full">
          {/* Dark mode toggle */}
          <button
            onClick={() => setIsDark((v) => !v)}
            aria-label="Toggle dark mode"
            className="flex items-center justify-center w-8 h-8 rounded-md text-ez-text-secondary hover:text-ez-text-primary hover:bg-ez-elevated transition-colors"
          >
            {isDark ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V4a1 1 0 0 1 1-1zm0 15a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1zm9-9h-1a1 1 0 1 1 0-2h1a1 1 0 1 1 0 2zM5 12a1 1 0 0 1-1 1H3a1 1 0 1 1 0-2h1a1 1 0 0 1 1 1zm12.657-5.657a1 1 0 0 1 0 1.414l-.707.707a1 1 0 1 1-1.414-1.414l.707-.707a1 1 0 0 1 1.414 0zM7.05 16.95a1 1 0 0 1 0 1.414l-.707.707a1 1 0 1 1-1.414-1.414l.707-.707a1 1 0 0 1 1.414 0zm10.607 0a1 1 0 0 1-1.414 0l-.707-.707a1 1 0 0 1 1.414-1.414l.707.707a1 1 0 0 1 0 1.414zM7.05 7.05a1 1 0 0 1-1.414 0l-.707-.707A1 1 0 0 1 6.343 4.93l.707.707a1 1 0 0 1 0 1.414zM12 7a5 5 0 1 0 0 10A5 5 0 0 0 12 7z"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
          <div className="font-semibold text-sm text-ez-text-primary capitalize">
            {user ? <span>{user}</span> : "Admin"} |
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="pl-1 capitalize text-ez-text-secondary hover:text-ez-text-primary transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutConfirm && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            className="bg-ez-surface border border-ez-border rounded-xl shadow-2xl overflow-hidden w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <PopupHeader
              title="Logout"
              onClose={() => setShowLogoutConfirm(false)}
            />
            <div className="px-6 py-5">
              <p className="text-sm text-ez-text-secondary text-center">
                Are you sure you want to logout?
              </p>
              <div className="mt-5 flex justify-center gap-3">
                <PillButton
                  variant="secondary"
                  onClick={() => setShowLogoutConfirm(false)}
                >
                  Cancel
                </PillButton>
                <PillButton variant="danger" onClick={handleLogout}>
                  Logout
                </PillButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div className="h-screen z-50 fixed w-20 bg-ez-sidebar text-white flex flex-col justify-between items-center py-4 border-r border-ez-border/20">
        <div className="-right-6 mb-12 transition transform ease-in-out duration-500 flex border-4 border-white bg-gray-900 hover:bg-blue-700 absolute top-2 p-3 rounded-full text-white hover:rotate-45">
          <Link to="/admin/dashboard">
            {resolveAutoBrand() === "vz" ? (
              <img src="/verizon.svg" alt="" className="w-4 h-4 object-contain" />
            ) : (
              <img src="/logo1.png" alt="" className="w-4 h-4 object-contain" />
            )}
          </Link>
        </div>
        <div className="flex flex-col items-center space-y-6 mt-[100px] px-2">
          {adminSidebarItems.map((item: any) =>
            item.name === "SMB" ? null : (
              <Link to={item.url} key={item.name}>
                <div className="flex flex-col items-center space-y-1 text-gray-200 cursor-pointer side-icon">
                  <item.icon
                    className={`${
                      location.pathname === item.url
                        ? "bg-white/15 p-1 rounded-lg text-2xl text-white"
                        : "text-2xl hover:bg-white/10"
                    }`}
                  />
                  <span className="text-xs">{item.name}</span>
                </div>
              </Link>
            )
          )}
        </div>
      </div>

      {/* Content */}
      <div className="content admin-layout min-h-screen pt-[66px] bg-ez-base">
        <Outlet />
      </div>
    </>
  );
});
export default SuperAdminLayouts;
